/**
 * Putting a panel under the control that opened it — ROADMAP 9.16.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A CENTRED DIALOG IS THE WRONG SHAPE FOR THIS ONE
 *
 * The exit ticket is opened from a 24-pixel button on a leg row, and it
 * opened in the middle of the screen. On a wide monitor with the drawer
 * split low that is most of a screen's width of mouse travel, twice — out
 * to the dialog and back to the row to check which leg it was. A ticket
 * that appears UNDER the button it came from is read without moving at all,
 * and the row it belongs to is still visible beside it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE THREE THINGS THAT GO WRONG, AND THE ORDER THEY ARE SOLVED IN
 *
 *   IT HANGS OFF THE BOTTOM    a leg near the foot of the drawer has no
 *                              room below it. FLIP: put the panel above the
 *                              button instead. Preferred over shrinking,
 *                              because a whole panel somewhere slightly
 *                              unexpected beats half a panel where it was
 *                              asked for.
 *   IT HANGS OFF THE SIDE      a button at the right edge of a narrow
 *                              drawer. SHIFT: slide the panel back inside,
 *                              which keeps it adjacent to the button
 *                              without moving the button.
 *   NEITHER SIDE FITS          a short viewport, a laptop with the browser
 *                              half-height. SHRINK: take the roomier side
 *                              and cap the height, so the panel scrolls
 *                              INSIDE itself rather than being cut off by
 *                              the window.
 *
 * Flip, then shift, then shrink. Shrinking first would scroll a panel that
 * had somewhere to go; shifting first would slide one that could simply
 * have flipped.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CENTRED ON THE BUTTON, NOT LEFT-ALIGNED TO IT
 *
 * The point of the change is mouse travel, and the cursor is on the button
 * when the panel appears. Centring puts every control in the panel within
 * half a panel-width of where the hand already is; aligning the left edge
 * puts the far side of a 420-pixel panel a full panel away. The shift above
 * then does the rest at the edges — and on a narrow drawer a centred panel
 * that shifts ends up looking deliberately right-aligned, which is what a
 * right-hand button wants anyway.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PURE, AND THE VIEWPORT IS AN ARGUMENT
 *
 * No `window`, no `getBoundingClientRect`, no React. Every number that
 * decides whether a trader can see their exit ticket is testable at any
 * screen size without a browser — which is the only way the "any screen
 * size, any drawer split" requirement can actually be checked rather than
 * eyeballed at the one size the author had open.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** The parts of a `DOMRect` this needs. Viewport coordinates, as `getBoundingClientRect` gives them. */
export interface AnchorBox {
  readonly top: number
  readonly left: number
  readonly bottom: number
  readonly width: number
}

export interface PanelSize {
  readonly width: number
  readonly height: number
}

export interface Viewport {
  readonly width: number
  readonly height: number
}

export interface PanelPlacement {
  /** `position: fixed` coordinates. */
  readonly top: number
  readonly left: number
  /** What the panel may grow to. Below this it scrolls inside itself. */
  readonly maxHeight: number
  /**
   * The width to APPLY, already the smaller of what the panel wants and
   * what the window allows.
   *
   * Not "the most it may have", which is what this used to return and was
   * wrong in a way only a wide monitor shows: the panel's own
   * `sm:max-w-[420px]` is a CLASS and the placement is an inline STYLE, so
   * a maxWidth of "the viewport less its margins" overrode the 420 and a
   * `w-full` panel stretched the whole screen.
   */
  readonly width: number
  /** Which way it opened. The caller animates from the right edge. */
  readonly side: 'below' | 'above'
}

export interface PlacementOptions {
  /** Clear air between the button and the panel. */
  readonly gap?: number
  /** Closest the panel may come to a window edge. */
  readonly margin?: number
  /**
   * Shortest the panel may be squeezed to before it stops shrinking and
   * simply sits inside the window scrolling.
   *
   * Without a floor, a viewport 200 px tall with the button in the middle
   * gives both sides ~80 px and the panel becomes a sliver with a scrollbar
   * — technically on screen and unusable.
   */
  readonly minHeight?: number
  /**
   * Never squeeze: overlap the button rather than scroll — ROADMAP 9.30.
   *
   * ───────────────────────────────────────────────────────────────────────
   * A SHORT PANEL SHOULD TAKE THE ROOM, NOT A SCROLLBAR
   *
   * Shrinking to the space beside the button is right for a panel that can
   * genuinely be tall — a long list, a chart. It is wrong for a fixed form
   * of six controls: there the cap produces a scrollbar inside a 250px
   * dialog, which reads as broken, and clips the very buttons the form
   * exists to offer.
   *
   * With this set the panel keeps its natural height and is clamped to the
   * VIEWPORT instead, so it rides over the row that opened it. Overlapping
   * a leg row costs nothing; the trader is looking at the panel.
   *
   * The viewport clamp still applies — a window shorter than the form has
   * no answer but to scroll, and that is the one case left.
   * ───────────────────────────────────────────────────────────────────────
   */
  readonly preferOverlap?: boolean
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(Math.max(v, lo), Math.max(lo, hi))

/**
 * Where to put the panel, given where the button is.
 *
 * `panel` is the size it WANTS — its natural height, measured once it has
 * rendered. The returned `maxHeight` is what it may actually have.
 */
export function placeAnchoredPanel(
  anchor: AnchorBox,
  panel: PanelSize,
  viewport: Viewport,
  options: PlacementOptions = {},
): PanelPlacement {
  const gap = options.gap ?? 6
  const margin = options.margin ?? 8
  const minHeight = options.minHeight ?? 160

  // ── width: shrink to fit, then centre on the button, then shift inside ──
  const maxWidth = Math.max(0, viewport.width - margin * 2)
  const width = Math.min(panel.width, maxWidth)
  const left = clamp(
    anchor.left + anchor.width / 2 - width / 2,
    margin,
    viewport.width - width - margin,
  )

  // ── height: flip if it does not fit below, then shrink if neither fits ──
  const roomBelow = viewport.height - anchor.bottom - gap - margin
  const roomAbove = anchor.top - gap - margin

  const side: 'below' | 'above' =
    panel.height <= roomBelow ? 'below'
    : panel.height <= roomAbove ? 'above'
    : roomBelow >= roomAbove ? 'below'
    : 'above'

  const room = side === 'below' ? roomBelow : roomAbove
  /*
   * `minHeight` can push this past the room available, which is deliberate
   * — see the option's own note. The clamp below is what keeps the result
   * on screen when it does, so the panel overlaps its button rather than
   * disappearing under the window edge.
   */
  const maxHeight = Math.min(
    options.preferOverlap === true
      ? panel.height
      : Math.max(Math.min(panel.height, room), minHeight),
    Math.max(0, viewport.height - margin * 2),
  )

  const wanted = side === 'below' ? anchor.bottom + gap : anchor.top - gap - maxHeight
  const top = clamp(wanted, margin, viewport.height - maxHeight - margin)

  return { top, left, maxHeight, width, side }
}

/**
 * Are these the same placement?
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS WHAT STOPS THE MEASURE-AND-PLACE LOOP
 *
 * The panel is re-measured after EVERY render, because its height changes
 * with its contents — switching to a resting exit grows a price field, a
 * refusal grows an error line — and a panel near the foot of the window has
 * to flip at that moment rather than grow off the bottom.
 *
 * Re-measuring means setting state, and a fresh object every time is an
 * infinite loop. Returning the PREVIOUS object when nothing moved is what
 * makes React bail out of the re-render, so the cycle settles in two passes:
 * place from a guess, measure, agree.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function samePlacement(a: PanelPlacement, b: PanelPlacement): boolean {
  return (
    a.top === b.top &&
    a.left === b.left &&
    a.maxHeight === b.maxHeight &&
    a.width === b.width &&
    a.side === b.side
  )
}
